/**
 * WaterFall组件 - 移动端瀑布流展示组件
 * 基于通用Waterfall组件实现，支持产品展示和广告图片的瀑布流布局
 * 注意：这是移动端专用组件，PC端不显示
 */

'use client';

import HomePageTitle from '@/app/(home-page)/components/HomePageTitle';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import Waterfall from '@/components/Waterfall/index';
import { useStore } from '@/providers/app-store-provider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import Image from '@/components/Image';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import SkeletonProductCard from '@/components/Product/SkeletonProductCard';
import { getComponentProductData } from '@/api/home';
import logger from '@/utils/client-logger';

// 导入统一的类型定义
import {
  ProductInfo,
  ProductTrackingParams,
  WaterFallItem,
  WaterFallModuleData,
  WaterFallProps,
} from '@/app/(home-page)/types';
import { env } from '@/utils/env';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useMergedRefs } from '@/hooks/useMergedRefs';

// Alt文本生成工具
const getAlt = (componentName: string, index: number, indexData?: any): string => {
  const baseAlt = indexData?.layout_title || componentName;
  return `${baseAlt} - Item ${index + 1}`;
};

// 获取产品数据辅助函数
const getProductData = (item: WaterFallItem) => {
  const { sku } = item;
  return {
    shop_sku: sku?.shop_sku || '',
    business_sku: sku?.business_sku || '',
    sku: sku?.sku || '',
    short_name: sku?.short_name || '',
    id: String(sku?.id || ''),
  };
};

// 获取分组数据辅助函数
const getGroupData = (item: WaterFallItem) => {
  const group = item.product_group;
  return {
    id: group?.id || '',
    name: group?.name || '',
  };
};

// 生成产品详情跳转参数
const generateProductJump = (productInfo: ProductInfo) => ({
  sku: { shop_sku: productInfo.shop_sku },
  Jump_target: productInfo.shop_sku,
  jump_type: 2,
});

// 瀑布流项目组件 - 广告图片类型
const AdImageItem = React.memo(
  ({
    item,
    index,
    indexData,
    moduleIndex,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  }: {
    item: WaterFallItem;
    index: number;
    indexData: WaterFallModuleData;
    moduleIndex: number;
    onTrack?: (params: ProductTrackingParams) => void;
    onExpTrack?: (params: ProductTrackingParams) => void;
    onValidExpTrack?: (params: ProductTrackingParams) => void;
  }) => {
    const hasTrackedRef = useRef(false); // 防止重复曝光

    // 获取埋点参数
    const trackingParams = useMemo((): ProductTrackingParams => {
      const productData = getProductData(item);
      const groupData = getGroupData(item);

      return {
        module_index: moduleIndex,
        module_name: indexData.component_id,
        mkt_index: index,
        mkt_index_sub: 0, // 修改为number类型
        mkt_jump_type: `${item.jump_type || ''}`,
        mkt_jump_content: `${item.Jump_target || ''}`,
        module_tab_name: '',
        coupon_id_list: '',
        coupon_name_list: '',
        shop_sku: productData.shop_sku,
        business_sku: productData.business_sku,
        product_sku: productData.sku,
        product_id: productData.id,
        product_name: productData.short_name,
        group_id: `${groupData.id}`,
        group_name: `${groupData.name}`,
        module_title: indexData.layout_title || '',
        module_id: String(indexData.blocks_advances_id || ''),
      };
    }, [item, index, indexData, moduleIndex]);

    // 曝光埋点
    const [expRef, inView] = useInView({
      threshold: 0.8,
      triggerOnce: true,
    });

    useEffect(() => {
      if (inView && onExpTrack && !hasTrackedRef.current) {
        hasTrackedRef.current = true;
        onExpTrack(trackingParams);
      }
    }, [inView, onExpTrack]);

    const { ref: validExposureRef } = useValidExposure({
      callback: () => onValidExpTrack?.(trackingParams),
    });

    // 点击埋点
    const handleClick = useCallback(() => {
      if (onTrack) {
        onTrack(trackingParams);
      }
    }, [onTrack, trackingParams]);

    // MagicJump props
    const jumpProps = useMemo(
      () => ({
        jump_type: item.jump_type || 0,
        Jump_target: item.Jump_target || '',
        ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
        ...(item.product_group?.id && {
          product_group: { id: String(item.product_group.id), path: item.product_group.path },
        }),
      }),
      [item],
    );

    const expMergedRef = useMergedRefs(expRef, validExposureRef);

    return (
      <div className="gg-wrapper rounded-lg overflow-hidden bg-white">
        <MagicJump
          prefetch
          item={jumpProps}
          onBeforeJump={handleClick}
          aria-label={getAlt('WaterFall', index, indexData)}
          className="block"
        >
          <div className="gg-image-only">
            <Image
              ref={expMergedRef}
              src={item.jump_image || item.image}
              alt={getAlt('WaterFall', index, indexData)}
              width={170}
              height={200}
              loading={moduleIndex < 4 ? 'eager' : 'lazy'}
              className="w-full h-auto object-cover block rounded-lg"
              priority={moduleIndex < 4}
            />
          </div>
        </MagicJump>
      </div>
    );
  },
);

// 瀑布流项目组件 - 产品类型
const ProductItem = React.memo(
  ({
    item,
    index,
    indexData,
    moduleIndex,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  }: {
    item: WaterFallItem;
    index: number;
    indexData: WaterFallModuleData;
    moduleIndex: number;
    onTrack?: (params: ProductTrackingParams) => void;
    onExpTrack?: (params: ProductTrackingParams) => void;
    onValidExpTrack?: (params: ProductTrackingParams) => void;
  }) => {
    const currency = useStore((state) => state.currency);
    const userInfo = useStore((state) => state.userInfo);
    const hasTrackedRef = useRef(false); // 防止重复曝光

    // 获取产品价格信息
    const productPriceInfo = useMemo(() => {
      if (!item.product_info) return {};
      return priceInfo(userInfo, item.product_info, currency);
    }, [userInfo, item.product_info, currency]);

    // 获取基础货币价格信息
    const basePriceInfo = useMemo(() => {
      if (!item.product_info) return {};
      return priceInfo(userInfo, item.product_info, {
        name: currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY,
      });
    }, [userInfo, item.product_info, currency]);

    // 获取埋点参数
    const trackingParams = useMemo((): ProductTrackingParams => {
      const product = item.product_info;
      if (!product) {
        const emptyParams: ProductTrackingParams = {
          module_index: moduleIndex,
          module_name: indexData.component_id,
          mkt_index: index,
          mkt_index_sub: 0, // 修改为number类型
          mkt_jump_type: '2',
          mkt_jump_content: '',
          module_tab_name: '',
          coupon_id_list: '',
          coupon_name_list: '',
          shop_sku: '',
          business_sku: '',
          product_sku: '',
          product_id: '',
          product_name: '',
          group_id: '',
          group_name: '',
          module_title: indexData.layout_title || '',
          product_price: 0,
          current_product_price: 0,
          base_current_product_price: 0,
          currency: currency.name,
          module_id: String(indexData.blocks_advances_id || ''),
        };
        return emptyParams;
      }

      return {
        module_index: moduleIndex,
        module_name: indexData.component_id,
        mkt_index: index,
        mkt_index_sub: 0, // 修改为number类型
        mkt_jump_type: '2',
        mkt_jump_content: `${product.id}`,
        module_tab_name: '',
        coupon_id_list: '',
        coupon_name_list: '',
        shop_sku: product.shop_sku,
        business_sku: product.business_sku || '',
        product_sku: product.sku,
        product_id: `${product.id}`,
        product_name: product.short_name,
        group_id: '',
        group_name: '',
        module_title: indexData.layout_title || '',
        product_price: parseFloat(productPriceInfo?.price || '0'),
        current_product_price: parseFloat(productPriceInfo?.sell_price || '0'),
        base_current_product_price: parseFloat(basePriceInfo?.sell_price || '0'),
        currency: currency.name,
        module_id: String(indexData.blocks_advances_id || ''),
      };
    }, [item, index, indexData, moduleIndex, productPriceInfo, basePriceInfo, currency]);

    // 曝光埋点
    const [expRef, inView] = useInView({
      threshold: 0.8,
      triggerOnce: true,
    });

    useEffect(() => {
      if (inView && onExpTrack && item.product_info && !hasTrackedRef.current) {
        hasTrackedRef.current = true;
        onExpTrack(trackingParams);
      }
    }, [inView, onExpTrack, item.product_info]);

    const { ref: validExposureRef } = useValidExposure({
      callback: () => onValidExpTrack?.(trackingParams),
    });

    // 点击埋点
    const handleClick = useCallback(() => {
      if (onTrack && item.product_info) {
        onTrack(trackingParams);
      }
    }, [onTrack, trackingParams, item.product_info]);

    const expMergedRef = useMergedRefs(expRef, validExposureRef);
    if (!item.product_info) return null;

    const jumpProps = generateProductJump(item.product_info);

    return (
      <div className="product-wrapper rounded-lg overflow-hidden bg-white">
        <MagicJump
          prefetch
          item={jumpProps}
          onBeforeJump={handleClick}
          aria-label={item.product_info.name}
          className="block"
        >
          <Image
            ref={expMergedRef}
            src={item.jump_image || item.image}
            alt={item.product_info.name}
            width={170}
            height={200}
            loading={moduleIndex < 4 ? 'eager' : 'lazy'}
            className="w-full h-auto object-cover block rounded-lg"
            priority={moduleIndex < 4}
          />
          <div className="product_info bg-white flex flex-col items-center justify-center h-[46px] px-[8px]">
            <span className="name text-[12px] truncate w-full text-left leading-tight">
              {item.product_info.short_name}
            </span>
            <div className="price mt-[2px] text-[12px] font-bb notranslate w-full text-left">
              <span>
                {formatPriceNumberWithSymbol(productPriceInfo?.sell_price || '0', currency)}
              </span>
            </div>
          </div>
        </MagicJump>
      </div>
    );
  },
);

// 瀑布流项目渲染组件
const WaterfallItemRenderer = React.memo(
  ({
    item,
    index,
    indexData,
    moduleIndex,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  }: {
    item: WaterFallItem;
    index: number;
    indexData: WaterFallModuleData;
    moduleIndex: number;
    onTrack: (params: ProductTrackingParams) => void;
    onExpTrack: (params: ProductTrackingParams) => void;
    onValidExpTrack: (params: ProductTrackingParams) => void;
  }) => {
    const trackParams = (params: ProductTrackingParams) => ({
      ...params,
      mkt_index_sub: Number(params.mkt_index_sub),
    });

    return item.type === '2' ? (
      <AdImageItem
        item={item}
        index={index}
        indexData={indexData}
        moduleIndex={moduleIndex}
        onTrack={(params) => onTrack(trackParams(params))}
        onExpTrack={(params) => onExpTrack(trackParams(params))}
        onValidExpTrack={(params) => onValidExpTrack(trackParams(params))}
      />
    ) : (
      <ProductItem
        item={item}
        index={index}
        indexData={indexData}
        moduleIndex={moduleIndex}
        onTrack={(params) => onTrack(trackParams(params))}
        onExpTrack={(params) => onExpTrack(trackParams(params))}
        onValidExpTrack={(params) => onValidExpTrack(trackParams(params))}
      />
    );
  },
);

/**
 * WaterFall主组件 - 移动端专用
 * 现在使用通用Waterfall组件实现
 */
export default function WaterFall({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: WaterFallProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isPC = deviceType === EPlatform.WEB;

  const [dynamicProductsData, setDynamicProductsData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 瀑布流数据
  const waterfallData = useMemo(() => {
    // 优先使用传入的静态数据 (例如 SEO 初始化时传入的带有商品数据的 indexData)
    const dataSource = dynamicProductsData ?? indexData;
    return dataSource?.products || [];
  }, [indexData, dynamicProductsData]);

  const hasProducts = waterfallData && waterfallData.length > 0;

  const { ref: inViewRef, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const currency = useStore((state) => state.currency);

  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  useEffect(() => {
    // 确保有必要的参数再发请求，且未尝试过获取数据
    if (
      inView &&
      !hasProducts &&
      !isLoading &&
      !hasAttemptedFetch &&
      indexData?.component_id &&
      indexData?.id
    ) {
      // 检查货币是否已准备好
      const currencyName = currency?.name || 'USD'; // 提供默认兜底值

      setIsLoading(true);
      getComponentProductData({
        component_id: indexData.component_id,
        id: Number(indexData.id),
        currency: currencyName,
      })
        .then((res: any) => {
          if (res && typeof res !== 'string' && res.data) {
            setDynamicProductsData(res.data);
          }
        })
        .catch((err) => {
          logger.error('获取商品数据失败:', err);
        })
        .finally(() => {
          setIsLoading(false);
          setHasAttemptedFetch(true);
        });
    }
  }, [
    inView,
    hasProducts,
    isLoading,
    hasAttemptedFetch,
    indexData?.component_id,
    indexData?.id,
    currency?.name,
  ]);

  // 渲染函数
  const renderItem = useCallback(
    (item: WaterFallItem, index: number) => (
      <WaterfallItemRenderer
        item={item}
        index={index}
        indexData={indexData}
        moduleIndex={moduleIndex}
        onTrack={onTrack}
        onExpTrack={onExpTrack}
        onValidExpTrack={onValidExpTrack}
      />
    ),
    [indexData, moduleIndex, onTrack, onExpTrack, onValidExpTrack],
  );

  // PC端不显示此组件
  if (isPC) {
    return null;
  }

  // 如果请求过数据并且数据为空，则不渲染该组件
  if (hasAttemptedFetch && !hasProducts) {
    return null;
  }

  return (
    <div
      ref={inViewRef}
      className="water-full py-[35px] pb-[27px] bg-cover bg-center"
      style={{
        backgroundColor: indexData.background_color || 'transparent',
      }}
    >
      {/* 标题组件 */}
      <HomePageTitle
        indexData={indexData}
        variant="default"
        displayCondition="mobile-only"
        titleTag="h2"
        subtitleTag="h4"
        isNew
        hasButton={false}
      />

      {/* 瀑布流容器 */}
      <div className="water-list mt-[20px] pl-[14px]">
        {hasProducts ? (
          <Waterfall
            data={waterfallData}
            renderItem={renderItem}
            columns={2}
            responsive
            baseWidth={375}
            defaultItemWidth={170}
            gap={8}
            minHeight={300}
          />
        ) : (
          <div className="grid grid-cols-2 gap-[8px] pr-[14px]">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={`waterfall-skeleton-${index}`} className="mb-[8px]">
                <SkeletonProductCard />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
